import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { billingAPI } from '../api';
import { 
  Crown, Star, Sparkles, CheckCircle2, Loader2, 
  Home, PartyPopper, Award
} from 'lucide-react';

const BillingSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (sessionId) {
        try {
          // First check checkout status to trigger any pending updates
          await billingAPI.getCheckoutStatus(sessionId);
        } catch (err) {
          console.error('Error checking checkout status:', err);
        }
      }
      
      // Then get the current subscription status
      try {
        const res = await billingAPI.getSubscriptionStatus();
        setStatus(res.data);
      } catch (err) {
        console.error('Error fetching subscription status:', err);
        setError('Unable to verify payment status. Please check your account.');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <Star className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Payment Received</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors flex items-center gap-2 mx-auto"
            data-testid="return-dashboard-btn"
          >
            <Home className="w-5 h-5" />
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isFounder = status?.founder_lifetime;
  const founderNumber = status?.founder_number;
  const constellationReward = status?.constellation_reward;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Celebration Animation */}
        <div className="relative">
          {/* Floating particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  backgroundColor: isFounder 
                    ? `hsl(${40 + Math.random() * 20}, 100%, ${60 + Math.random() * 20}%)`
                    : `hsl(${240 + Math.random() * 40}, 100%, ${60 + Math.random() * 20}%)`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 2}s`,
                }}
              />
            ))}
          </div>

          {/* Main card */}
          <div className={`relative rounded-3xl p-8 text-center border-2 ${
            isFounder 
              ? 'bg-gradient-to-b from-amber-900/40 to-slate-800/60 border-amber-500/50' 
              : 'bg-gradient-to-b from-indigo-900/40 to-slate-800/60 border-indigo-500/50'
          }`}>
            {/* Icon */}
            <div className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
              isFounder 
                ? 'bg-gradient-to-br from-amber-400 to-orange-500' 
                : 'bg-gradient-to-br from-indigo-400 to-purple-500'
            }`}>
              {isFounder ? (
                <Crown className="w-12 h-12 text-white" />
              ) : (
                <CheckCircle2 className="w-12 h-12 text-white" />
              )}
            </div>

            {/* Success message */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <PartyPopper className={isFounder ? 'text-amber-400' : 'text-indigo-400'} />
              <h1 className="text-3xl font-bold text-white">
                {isFounder ? 'Welcome, Founding Family!' : 'Welcome to the Family!'}
              </h1>
              <PartyPopper className={isFounder ? 'text-amber-400' : 'text-indigo-400'} />
            </div>

            {isFounder ? (
              <>
                {/* Founder specific content */}
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full mb-4">
                    <Award className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-300 font-bold text-xl">
                      Founding Family #{founderNumber}
                    </span>
                  </div>
                  <p className="text-slate-300">
                    You're one of our first 100 families! Thank you for believing in us from the start.
                  </p>
                </div>

                {/* Rewards */}
                <div className="bg-slate-800/50 rounded-2xl p-6 mb-6">
                  <h2 className="text-lg font-semibold text-amber-300 mb-4 flex items-center justify-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Your Founder Rewards
                  </h2>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-8 h-8 rounded-full bg-amber-500/30 flex items-center justify-center">
                        <Crown className="w-4 h-4 text-amber-400" />
                      </div>
                      <span>Lifetime unlimited access</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-8 h-8 rounded-full bg-amber-500/30 flex items-center justify-center">
                        <Award className="w-4 h-4 text-amber-400" />
                      </div>
                      <span>Exclusive Founder Badge</span>
                    </div>
                    {constellationReward && (
                      <div className="flex items-center gap-3 text-white">
                        <div className="w-8 h-8 rounded-full bg-amber-500/30 flex items-center justify-center">
                          <Star className="w-4 h-4 text-amber-400" />
                        </div>
                        <span>Your Constellation: <strong className="text-amber-300">{constellationReward}</strong></span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Family plan content */}
                <p className="text-slate-300 mb-6">
                  Your family now has unlimited access to all learning adventures, creative tools, and magical experiences!
                </p>

                <div className="bg-slate-800/50 rounded-2xl p-6 mb-6">
                  <h2 className="text-lg font-semibold text-indigo-300 mb-4">What's Unlocked</h2>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <span>Unlimited lessons</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <span>All creative tools</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <span>All children included</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <span>Priority support</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* CTA Button */}
            <button
              onClick={() => navigate('/temple')}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                isFounder 
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white' 
                  : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white'
              }`}
              data-testid="return-temple-btn"
            >
              <Home className="w-5 h-5" />
              Enter the Temple
            </button>
          </div>
        </div>
      </div>

      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.8; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 0.4; }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default BillingSuccessPage;
